import React, { useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import { deleteOrganizationSpot, getOrganizationSpots } from '@/services/api/organizations';

import If from '@/components/If';
import LoadData from '@/components/LoadData';
import Typography from '@/components/Typography';
import { translations } from '@/locale';
import Loading from '@/components/Loading';
import TimeSlotModal from '@/components/Modal/TimeSlotModal';
import Modal from '@/components/Modal';
import Placeholder from '@/components/Placeholder';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';

const text = translations.pages.organizations.tabs.diningSlots;

export interface Props {
  id: string;
}

const OrganizationDiningSlotsTab: React.FC<Props> = ({ id }) => {
  const reloadRef = useRef<() => void>();

  return (
    <Card>
      <CardHeader className="flex justify-between">
        <div>
          <Typography is="h1" size="xl" weight="bold">
            <FormattedMessage id={text.title} />
          </Typography>
          <Typography is="p" size="sm">
            Add limits to individual dining slots to prevent overwhelmed dining areas.
          </Typography>
        </div>
        <Modal modal={TimeSlotModal} orgId={id} type="add" onSaved={() => reloadRef.current?.()}>
          {({ open }) => (
            <Button variant="outline" onClick={open}>
              Add slot
            </Button>
          )}
        </Modal>
      </CardHeader>
      <CardContent>
        <LoadData id={id} load={getOrganizationSpots}>
          {({ value: slots, loading, reload }) => {
            reloadRef.current = reload;
            return (
              <React.Fragment>
                <Loading visible={loading} />

                <If
                  condition={!slots || loading}
                  then={() => (
                    <Placeholder
                      image="cards"
                      size="sm"
                      title="No dining slots"
                      description="Start adding time slots to your organization to enable dining reservations."
                    />
                  )}
                  else={() => (
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Slot</TableHead>
                          <TableHead>Limit</TableHead>
                          <TableHead></TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {slots.map((slot) => (
                          <TableRow key={slot.id}>
                            <TableCell>
                              {slot.startTime} - {slot.endTime}
                            </TableCell>
                            <TableCell>{slot.limit}</TableCell>
                            <TableCell className="block whitespace-nowrap">
                              <Button
                                variant="destructive"
                                onClick={() => deleteOrganizationSpot(id, slot.id).then(reload)}
                              >
                                Delete
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                />
              </React.Fragment>
            );
          }}
        </LoadData>
      </CardContent>
    </Card>
  );
};

export default OrganizationDiningSlotsTab;
